import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Cookie } from 'lucide-react';

declare global {
  interface HubSpotConversationsAPI {
    widget: { load: () => void };
  }

  interface Window {
    _hsq?: unknown[];
    HubSpotConversations?: HubSpotConversationsAPI;
    hsConversationsOnReady?: Array<() => void>;
  }
}

const CONSENT_KEY = 'hubspot-consent';

const ConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState<string | null>(null);

  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    setConsent(storedConsent);
    
    if (!storedConsent) {
      // Show banner after a short delay to avoid layout shift
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else if (storedConsent === 'accepted') {
      enableHubSpotTracking();
    } else if (storedConsent === 'declined') {
      disableHubSpotTracking();
    }
  }, []);

  const enableHubSpotTracking = () => {
    // Enable full HubSpot tracking and personalization
    window._hsq = window._hsq || [];
    window._hsq.push(['doNotTrack', {track: true}]);
    window._hsq.push(['trackPageView']);
    window._hsq.push([
      'addIdentityListener',
      function (hstc, hssc, hsfp) {
        if (process.env.NODE_ENV === 'development') { console.log('HubSpot identity established:', { hstc, hssc, hsfp }); }
      },
    ]);

    // Load HubSpot chat widget when consent is granted
    if (window.HubSpotConversations?.widget?.load) {
      window.HubSpotConversations.widget.load();
    } else {
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(() => {
        window.HubSpotConversations?.widget?.load();
      });
    }
  };

  const disableHubSpotTracking = () => {
    // Disable HubSpot tracking via consent API
    window._hsq = window._hsq || [];
    window._hsq.push(['doNotTrack', {track: false}]);
  };

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setShowBanner(false);
    enableHubSpotTracking();
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setConsent('declined');
    setShowBanner(false);
    disableHubSpotTracking();
  };

  if (!showBanner || consent) {
    return null;
  }

  return (
    <div className="fixed bottom-16 left-0 right-0 z-50 bg-card border-t shadow-elegant">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 flex-1">
            <Cookie className="h-5 w-5 text-primary" />
            <div className="text-sm">
              <p className="font-medium">Cookie Consent</p>
              <p className="text-muted-foreground">
                We use cookies and tracking to improve your experience via HubSpot.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={handleDecline}>
              Decline
            </Button>
            <Button size="sm" onClick={handleAccept}>
              Accept
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDecline}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
